"use client";

import React from "react";
import { Globe, Palette, ShieldCheck, Key, FileCheck, Server, Layers3 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const benefits = [{
  title: "Branded client environments",
  description: "Present EloRAG under your own agency or client-facing identity with custom styling, logos, and branded RAG delivery.",
  icon: Palette
}, {
  title: "Custom routing and domain fit",
  description: "Deliver through branded domains and client-facing environments that feel native to your own service model.",
  icon: Globe
}, {
  title: "No need to build the control layer",
  description: "EloRAG handles approved-only retrieval, citation logic, refusal behavior, and operational control underneath the branded experience.",
  icon: ShieldCheck
}, {
  title: "No absorbed model usage costs",
  description: "The BYO-Key model keeps variable LLM and OCR usage with the client instead of turning it into agency cost exposure.",
  icon: Key
}, {
  title: "Controlled client knowledge delivery",
  description: "Use governed workflows so answers come from approved content instead of broad, unmanaged document access.",
  icon: FileCheck
}, {
  title: "Predictable scaling with capacity",
  description: "Expand storage, vectors, or queries with Capacity Packs as tenant usage grows, without changing the whole delivery model.",
  icon: Server
}];
export default function AgencyBenefits() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="agency-benefits" className="bg-secondary/20 py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={cn("mx-auto mb-12 max-w-3xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            <span>Agency advantage</span>
          </div>

          <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Built for resale, control, and branded delivery
          </h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            EloRAG gives agencies a cleaner way to deliver branded RAG environments without
            taking on the full retrieval and governance burden themselves.
          </p>
        </div>

        <div ref={gridRef} className={cn("grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3 transition-all duration-700 ease-out", isGridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={benefit.title} data-index={i} className="h-full rounded-2xl border border-border/70 bg-card p-6 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)] transition-all duration-300 hover:border-primary/25 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.15)]" style={{
            transitionDelay: `${i * 90}ms`
          }}>
                <div className="mb-4 w-fit rounded-xl bg-primary/10 p-3">
                  <Icon className="size-5 text-primary" aria-hidden="true" />
                </div>

                <h3 className="mb-3 text-lg font-semibold text-foreground">
                  {benefit.title}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {benefit.description}
                </p>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}